import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAccountDetail } from "@/hooks/accounts/useAccountDetail.ts";
import { useAccountMetrics } from "@/hooks/accounts/useAccountMetrics.ts";
import { useBalanceTrend } from "@/hooks/useBalanceTrend";
import { useTransactions, useCreateTransaction } from "@/hooks/useTransactions";
import { useFetchCategories } from "@metadataHooks";
import {
  useDeleteAccount,
  useUpdateAccount,
  useFetchAccounts,
} from "@accountHooks";
import { AccountMetricsCards } from "@/components/account-detail/AccountMetricsCards";
import { BalanceTrendChart } from "@/components/charts/BalanceTrendChart";
import { TransactionFilters } from "@/components/account-detail/TransactionFilters";
import { TransactionList } from "@/components/account-detail/TransactionList";
import { CurrencyToggle } from "@/components/currency-toggle/CurrencyToggle";
import { AccountForm } from "@/components/account-form/AccountForm";
import { TransactionForm, TransactionFormData } from "@/components/transaction-form/TransactionForm";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ErrorFallback } from "@/components/ErrorFallback";
import type {
  TransactionFilters as Filters,
  Transaction,
} from "@/types/account-drilldown";
import { toast } from "sonner";

function AccountDetailContent() {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const deleteAccount = useDeleteAccount();
  const updateAccount = useUpdateAccount();
  const createTransaction = useCreateTransaction();

  // State for dialogs
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isTransactionDialogOpen, setIsTransactionDialogOpen] = useState(false);

  // State for transaction filters
  const [filters, setFilters] = useState<Filters>({
    timePeriod: "last30days",
    categoryId: undefined,
    dateFrom: undefined,
    dateTo: undefined,
  });

  // Fetch account data
  const {
    data: account,
    isLoading: accountLoading,
    error: accountError,
  } = useAccountDetail(accountId || "");

  // Fetch account metrics
  const { metrics, isLoading: metricsLoading } = useAccountMetrics(
    accountId || "",
    filters,
  );

  // Fetch balance trend
  const { data: balanceTrend, isLoading: trendLoading } = useBalanceTrend(
    accountId || "",
    filters.timePeriod,
  );

  // Fetch categories for filter
  const { data: categories } = useFetchCategories();

  // Fetch all accounts for transaction form
  const { data: accounts } = useFetchAccounts();

  // Build transaction query filters
  const transactionQueryFilters = {
    accountId: accountId || "",
    categoryId: filters.categoryId,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
  };

  // Apply time period to date range if not custom
  if (filters.timePeriod !== "custom" && filters.timePeriod !== "alltime") {
    const now = new Date();
    const today = now.toISOString().split("T")[0];

    switch (filters.timePeriod) {
      case "last7days": {
        const date = new Date(now);
        date.setDate(date.getDate() - 7);
        transactionQueryFilters.dateFrom = date.toISOString().split("T")[0];
        transactionQueryFilters.dateTo = today;
        break;
      }
      case "last30days": {
        const date = new Date(now);
        date.setDate(date.getDate() - 30);
        transactionQueryFilters.dateFrom = date.toISOString().split("T")[0];
        transactionQueryFilters.dateTo = today;
        break;
      }
      case "last90days": {
        const date = new Date(now);
        date.setDate(date.getDate() - 90);
        transactionQueryFilters.dateFrom = date.toISOString().split("T")[0];
        transactionQueryFilters.dateTo = today;
        break;
      }
    }
  }

  // Fetch filtered transactions
  const { data: transactions, isLoading: transactionsLoading } =
    useTransactions(transactionQueryFilters);

  // Handle navigation back to accounts
  const handleBack = () => {
    navigate("/accounts");
  };

  // Handle edit account
  const handleEdit = () => {
    setIsEditDialogOpen(true);
  };

  // Handle account save
  const handleAccountSave = async (data) => {
    try {
      await updateAccount.mutateAsync({ id: accountId!, ...data });
      toast.success("Account updated successfully");
      setIsEditDialogOpen(false);
    } catch (error) {
      toast.error(error.message || "Failed to update account");
    }
  };

  // Handle account edit cancel
  const handleAccountCancel = () => {
    setIsEditDialogOpen(false);
  };

  // Handle delete account
  const handleDelete = async () => {
    if (!accountId) return;

    try {
      await deleteAccount.mutateAsync(accountId);
      toast.success("Account deleted successfully");
      navigate("/accounts");
    } catch (error) {
      toast.error(error.message || "Failed to delete account");
    }
  };

  // Handle add transaction
  const handleAddTransaction = () => {
    setIsTransactionDialogOpen(true);
  };

  // Handle transaction save
  const handleTransactionSave = async (data: TransactionFormData) => {
    try {
      await createTransaction.mutateAsync({ ...data, sub_category_id: data.sub_category_id });
      toast.success("Transaction created successfully");
      setIsTransactionDialogOpen(false);
    } catch (error) {
      toast.error(error.message || "Failed to create transaction");
    }
  };

  // Handle transaction cancel
  const handleTransactionCancel = () => {
    setIsTransactionDialogOpen(false);
  };

  // Handle transaction click
  const handleTransactionClick = (transaction: Transaction) => {
    navigate(`/transactions/${transaction.id}`)
  };

  // Handle loading state
  if (accountLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="h-8 w-48 bg-muted animate-pulse rounded" />
            <div className="h-4 w-32 bg-muted animate-pulse rounded mt-2" />
          </div>
        </div>
        <div className="h-32 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  // Handle error state
  if (accountError || !account) {
    return (
      <ErrorFallback
        error={accountError}
        title="Account Not Found"
        description="The account you are looking for does not exist or has been deleted."
        resetError={() => navigate("/accounts")}
        showHomeButton={false}
      />
    );
  }

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: account.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Account Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-3 sm:gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className="min-h-[44px] min-w-[44px]">
            <ArrowLeft className="h-5 w-5" />
            <span className="sr-only">Back to accounts</span>
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight break-words">
                {account.name}
              </h1>
              <Badge
                variant={account.status === "active" ? "default" : "outline"}>
                {account.status === "active" ? "Active" : "Inactive"}
              </Badge>
            </div>
            {account.nickname && (
              <p className="text-sm text-muted-foreground mt-1">
                Nickname: {account.nickname}
              </p>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-2 text-sm text-muted-foreground">
              <span className="capitalize">
                {account.type.replace("_", " ")}
                <br></br>
                {account.bank_name && (
                  <span className="capitalize">{account.bank_name}</span>
                )}
              </span>
              <span className="hidden sm:inline" aria-hidden="true">
                •
              </span>
              <span className="capitalize">
                Base Currency: {account.currency}
              </span>
              <span className="hidden sm:inline" aria-hidden="true">
                •
              </span>
              <span className="capitalize">
                Account Number: {account.account_number}
              </span>
              <span className="hidden sm:inline" aria-hidden="true">
                •
              </span>
              <span className="break-words">
                Current Balance:{" "}
                <span className="font-semibold text-foreground">
                  {formatCurrency(account.balance)}
                </span>
              </span>
              <span className="hidden sm:inline" aria-hidden="true">
                •
              </span>
              <span>
                Created {new Date(account.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={handleAddTransaction}
            className="min-h-[44px] flex-1 sm:flex-initial">
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Add Transaction</span>
            <span className="sm:hidden">Add</span>
          </Button>
          <Button
            variant="outline"
            onClick={handleEdit}
            className="min-h-[44px]"
            aria-label="Edit account">
            <Pencil className="h-4 w-4 sm:mr-2" aria-hidden="true" />
            <span className="hidden sm:inline">Edit</span>
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="min-h-[44px]"
                aria-label="Delete account">
                <Trash2 className="h-4 w-4 sm:mr-2" aria-hidden="true" />
                <span className="hidden sm:inline">Delete</span>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete "{account.name}"?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete this account and all associated
                  transactions. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Delete Account
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Live region for dynamic updates */}
      <div
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true">
        {transactionsLoading
          ? "Loading transactions"
          : transactions
            ? `${transactions.length} transactions loaded`
            : "No transactions"}
      </div>

      {/* Currency Toggle */}
      <CurrencyToggle
        balance={account.balance}
        baseCurrency={account.currency}
      />

      {/* Account Metrics Cards */}
      <AccountMetricsCards
        metrics={metrics}
        currency={account.currency}
        isLoading={metricsLoading}
      />

      {/* Balance Trend Chart */}
      <BalanceTrendChart
        data={balanceTrend || []}
        currency={account.currency}
        timePeriod={filters.timePeriod}
        isLoading={trendLoading}
      />

      {/* Transaction Filters and List */}
      <Card>
        <CardHeader>
          <CardTitle>Transactions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <TransactionFilters
            filters={filters}
            onFiltersChange={setFilters}
            categories={categories || []}
          />
          <TransactionList
            transactions={transactions || []}
            isLoading={transactionsLoading}
            onTransactionClick={handleTransactionClick}
          />
        </CardContent>
      </Card>

      {/* Edit Account Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle>Edit Account</DialogTitle>
          </DialogHeader>
          <AccountForm
            account={account}
            onSave={handleAccountSave}
            onCancel={handleAccountCancel}
          />
        </DialogContent>
      </Dialog>

      {/* Add Transaction Dialog */}
      <Dialog
        open={isTransactionDialogOpen}
        onOpenChange={setIsTransactionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Transaction</DialogTitle>
          </DialogHeader>
          <TransactionForm
            accounts={accounts || []}
            categories={categories || []}
            defaultAccountId={accountId}
            defaultCurrency={account.currency}
            onSave={handleTransactionSave}
            onCancel={handleTransactionCancel}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AccountDetail() {
  return (
    <ErrorBoundary>
      <AccountDetailContent />
    </ErrorBoundary>
  );
}
